import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { notifications } from "@mantine/notifications"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { fetchProviderCredits, refreshProviderCredits, updateProviderCredit } from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import { requireAdmin } from "../../lib/route-guards"
import { ProviderCreditsView } from "./-provider-credits/ProviderCreditsView"
import type { IProviderCreditEditValues } from "./-provider-credits/ProviderCreditsView/interfaces"

export const Route = createFileRoute("/_auth/provider-credits")({
  beforeLoad: requireAdmin,
  component: ProviderCreditsPage,
})

function ProviderCreditsPage(): React.JSX.Element {
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.admin.providerCredits(),
    queryFn: fetchProviderCredits,
  })

  const refreshMutation = useMutation({
    mutationFn: refreshProviderCredits,
    onSuccess: (fresh) => {
      queryClient.setQueryData(queryKeys.admin.providerCredits(), fresh)
    },
    onError: () => {
      notifications.show({ color: "danger", message: i18n._(msg`Failed to refresh provider status`) })
    },
  })

  const saveMutation = useMutation({
    mutationFn: ({ provider, values }: { provider: string; values: IProviderCreditEditValues }) =>
      updateProviderCredit(provider, values),
    onSuccess: () => {
      notifications.show({ color: "success", message: i18n._(msg`Provider settings saved`) })
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.providerCredits() })
    },
    onError: () => {
      notifications.show({ color: "danger", message: i18n._(msg`Failed to save provider settings`) })
    },
  })

  return (
    <ProviderCreditsView
      providers={data ?? []}
      isLoading={isLoading}
      isRefreshing={refreshMutation.isPending}
      isSaving={saveMutation.isPending}
      onRefresh={() => refreshMutation.mutate()}
      onSave={(provider, values) => saveMutation.mutate({ provider, values })}
    />
  )
}
